// @flow
import { Record } from "immutable"
import moment from "moment"
import { format, addMonths, addDays, isBefore, isAfter } from "date-fns"
import * as R from "ramda"

import constants from "../consts/config"
import { DATE_MODES, DATE_FORMAT, DateMode } from "../consts/date"
import type { DateTypes } from "./SearchDate/SearchDate"

type Input = {
  mode?: DateMode,
  from?: string,
  to?: string,
  minStayDays?: number,
  maxStayDays?: number,
  final?: boolean,
  isDefault?: boolean,
  type?: DateTypes,
}

const SearchDateRecord = Record({
  mode: DATE_MODES.SINGLE,
  from: format(new Date(), DATE_FORMAT),
  to: format(new Date(), DATE_FORMAT),
  minStayDays: 2,
  maxStayDays: 10,
  final: true,
  isDefault: true,
  type: null,
})

class SearchDate extends SearchDateRecord {
  constructor(raw: Input = {}) {
    const input = { ...raw }
    switch (input.mode) {
      case DATE_MODES.SINGLE:
        if (input.from) {
          input.to = input.from
        }
        break

      case DATE_MODES.INTERVAL:
        if (input.from === input.to) {
          input.mode = DATE_MODES.SINGLE
        }
        break

      case DATE_MODES.ANYTIME:
        input.from = format(addDays(new Date(), 1), DATE_FORMAT)
        input.to = format(addMonths(new Date(), Number(constants.maxSearchMonths)), DATE_FORMAT)
        break

      case undefined:
        input.mode = input.from === input.to ? DATE_MODES.SINGLE : DATE_MODES.INTERVAL
    }

    super(input)
  }

  // utils
  // =====
  includes(date: string): boolean {
    if (this.mode === DATE_MODES.SINGLE || this.mode === DATE_MODES.INTERVAL) {
      // that add/subtract mambo jambo makes the function inclusive
      // moment().isBetween() is exclusive by default
      return moment
        .utc(date)
        .isBetween(
          moment.utc(this.from).subtract(1, "day"),
          moment.utc(this.to).add(1, "day"),
          "day",
        )
    }

    return false
  }

  isBefore(date: string): boolean {
    if (this.mode === DATE_MODES.SINGLE || this.mode === DATE_MODES.INTERVAL) {
      return isBefore(this.from, date)
    }

    return false
  }

  isAfter(date: string): boolean {
    if (this.mode === DATE_MODES.SINGLE || this.mode === DATE_MODES.INTERVAL) {
      return isAfter(this.to, date)
    }

    return false
  }

  isInPast(): boolean {
    if (this.mode === DATE_MODES.SINGLE || this.mode === DATE_MODES.INTERVAL) {
      return isBefore(this.to, format(new Date(), DATE_FORMAT))
    }

    return false
  }

  // getters
  // =======
  getId(): string {
    switch (this.mode) {
      case DATE_MODES.TIME_TO_STAY:
        return R.uniq([this.minStayDays, this.maxStayDays]).join("-")

      case DATE_MODES.SINGLE:
      case DATE_MODES.INTERVAL:
        return R.uniq([this.from, this.to]).join("_")

      default:
        return this.mode
    }
  }

  isSingleDate() {
    return this.mode === DATE_MODES.SINGLE
  }

  getSingleDateString(FORMAT: string): string {
    return this.mode !== DATE_MODES.NO_RETURN ? format(this.from, FORMAT) : ""
  }

  static fromPlainJS(input: Input): SearchDate {
    return new SearchDate(input)
  }

  static toIntervalAround(date: SearchDate, dayCount: number = 1) {
    let subtract = dayCount
    const diff = moment(date.get("from"), DATE_FORMAT)
      .subtract(dayCount, "days")
      .diff(moment(), "days")
    if (diff < 0) {
      subtract = 0
    }
    return date
      .set("mode", DATE_MODES.INTERVAL)
      .set(
        "from",
        moment(date.get("from"), DATE_FORMAT)
          .subtract(subtract, "days")
          .format(DATE_FORMAT),
      )
      .set(
        "to",
        moment(date.get("from"), DATE_FORMAT)
          .add(dayCount, "days")
          .format(DATE_FORMAT),
      )
  }

  static toTimeToStayAround(date: SearchDate, min: number = 2, max: number = 4) {
    return date
      .set("mode", DATE_MODES.TIME_TO_STAY)
      .set("minStayDays", min)
      .set("maxStayDays", max)
  }
}

export default SearchDate
